/* eslint-disable import/no-extraneous-dependencies, react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Button } from '@ui';
import api from '@utils/axios';
import SendInvoiceModal from './index';
import SendInvoiceModalView from './SendInvoiceModalView';
const meta = {
    title: 'Features/Invoices/SendInvoiceModal',
    component: SendInvoiceModalView,
    parameters: { layout: 'fullscreen' }
};
export default meta;
// A tiny inline PDF so the preview pane has something real to render without a server.
const SAMPLE_PDF = 'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL01lZGlhQm94WzAgMCA2MTIgNzkyXT4+CmVuZG9iagp4cmVmCjAgNAowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMDkgMDAwMDAgbiAKMDAwMDAwMDA1NiAwMDAwMCBuIAowMDAwMDAwMTExIDAwMDAwIG4gCnRyYWlsZXIKPDwvU2l6ZSA0L1Jvb3QgMSAwIFI+PgpzdGFydHhyZWYKMTkwCiUlRU9G';
const baseArgs = {
    invoiceId: 2573,
    customerEmail: 'colleen.mey15633@leobit.com,llozynska@leobit.com',
    subject: 'Invoice INM251359 from Flash Point Fire Protection',
    body: 'Your invoice for Flash Point Fire Protections is due:\n\n' +
        'Invoice #: INM251359\nAmount: $4.00\nDue Date: November 10, 2026\n\n' +
        'Click the link in the email to view or pay your invoice.',
    pdfPath: SAMPLE_PDF,
    defaultAttachPdf: true,
    hasLatestReport: true
};
function Harness(args) {
    const [isOpen, setIsOpen] = useState(true);
    return (React.createElement("div", { style: { padding: '2.4rem' } },
        React.createElement(Button, { variant: "primary", onClick: () => setIsOpen(true) }, "Open Send Email"),
        React.createElement(SendInvoiceModalView, Object.assign({}, args, { isOpen: isOpen, onClose: () => setIsOpen(false) }))));
}
/**
 * The dialog as it appears on the invoice show page.
 *
 * To check IP-15897, resize the browser window rather than using browser zoom — zoom on a large
 * monitor leaves enough height that the footer still fits, which is why the bug was originally
 * not reproducible. The reported condition is roughly a 1280x653 viewport.
 *
 * At any window size, "Send and Close" must stay visible and the body should scroll beneath it.
 */
export const Default = {
    render: () => React.createElement(Harness, Object.assign({}, baseArgs))
};
/** Invoice with no generated report — the "Add Latest Report" checkbox is absent. */
export const WithoutLatestReport = {
    render: () => React.createElement(Harness, Object.assign({}, baseArgs, { hasLatestReport: false }))
};
/** Tenant that defaults both "Send me a copy" and "Attach PDF" to on. */
export const TenantDefaultsOn = {
    render: () => React.createElement(Harness, Object.assign({}, baseArgs, { defaultBccMe: true, defaultAttachPdf: true }))
};
/** No PDF available — the preview pane is omitted and the form takes the full width. */
export const NoPdfPreview = {
    render: () => React.createElement(Harness, Object.assign({}, baseArgs, { pdfPath: undefined }))
};
const CALLOUT_ID = 'invoice-not-emailed-callout';
const CALLOUT_TEXT = 'It looks like this invoice has not been emailed to your customer.';
/**
 * Invoice show page as the island is mounted in `invoices/show.html.erb`:
 * server-rendered not-emailed callout + header Send control + the React island.
 * Axios is stubbed so Send can return 200 or 422 without Rails/SES.
 */
function ShowPageHarness() {
    const [failNextSend, setFailNextSend] = useState(false);
    useEffect(() => {
        const originalPost = api.post.bind(api);
        if (!document.querySelector('meta[name="csrf-token"]')) {
            const csrfMeta = document.createElement('meta');
            csrfMeta.setAttribute('name', 'csrf-token');
            csrfMeta.setAttribute('content', 'storybook');
            document.head.appendChild(csrfMeta);
        }
        api.post = (() => {
            if (failNextSend) {
                const failure = new Error('send failed');
                failure.response = {
                    data: { message: 'There was a problem sending the invoice email. Please try again.' }
                };
                return Promise.reject(failure);
            }
            return Promise.resolve({ data: { message: 'Invoice Successfully Sent!' } });
        });
        return () => {
            api.post = originalPost;
        };
    }, [failNextSend]);
    return (React.createElement("div", { style: { padding: '2.4rem', maxWidth: '80rem' } },
        React.createElement("p", null,
            React.createElement("button", { type: "button", id: "simulate-failed-send", onClick: () => setFailNextSend(true) }, "Simulate failed send")),
        React.createElement("button", { type: "button", id: "invoice-send-email-link" }, "Send"),
        React.createElement("article", { className: "qmb-blurb--callout--attention", id: CALLOUT_ID },
            React.createElement("p", null,
                CALLOUT_TEXT,
                ' ',
                React.createElement("button", { type: "button", id: "invoice-send-email-callout-link" }, "Click here to send.")),
            "If you sent this another way, you can disregard this warning."),
        React.createElement(SendInvoiceModal, { invoiceId: baseArgs.invoiceId, customerEmail: baseArgs.customerEmail, subject: baseArgs.subject, body: baseArgs.body, pdfPath: baseArgs.pdfPath, hasLatestReport: baseArgs.hasLatestReport, defaultAttachPdf: baseArgs.defaultAttachPdf, triggerIds: ['invoice-send-email-link', 'invoice-send-email-callout-link'], dismissElementId: CALLOUT_ID })));
}
export const ShowPageWithNotEmailedCallout = {
    render: () => React.createElement(ShowPageHarness, null)
};
